import com.voter.common.Constants
import com.voter.models.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import org.w3c.dom.events.Event
import kotlin.random.Random

fun main() {
    window.addEventListener("load", { _: Event ->
        App().start()
    })
}

class App {
    private val root = document.getElementById("root") as HTMLElement
    
    fun start() {
        render()
        setupRouting()
    }
    
    private fun setupRouting() {
        window.addEventListener("popstate", { _: Event ->
            render()
        })
    }
    
    private fun render() {
        val path = window.location.pathname
        val params = js("new URLSearchParams(window.location.search)")
        val pollId = params.get("id") as String?
        
        root.innerHTML = ""
        
        when (path) {
            "/" -> renderCreatePollPage()
            "/vote" -> {
                if (pollId != null) {
                    renderVotingPage(pollId)
                } else {
                    renderError("Poll ID is required. Please use a valid poll link.")
                }
            }
            "/status" -> {
                if (pollId != null) {
                    renderStatusPage(pollId)
                } else {
                    renderError("Poll ID is required for status page.")
                }
            }
            "/admin" -> {
                if (pollId != null) {
                    renderAdminPage(pollId)
                } else {
                    renderError("Poll ID is required for admin panel.")
                }
            }
            else -> renderCreatePollPage()
        }
    }
    
    private fun renderError(message: String) {
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <div class="error-message">$message</div>
                <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Go Home</button>
            </div>
        """.trimIndent()
    }
    
    private fun renderCreatePollPage() {
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <div class="card">
                    <h2 class="subtitle">${Constants.Strings.CREATE_POLL_TITLE}</h2>
                    <div id="messages"></div>
                    
                    <!-- Toggle between form and JSON input -->
                    <div class="flex-row" style="margin-bottom: var(--spacing-md);">
                        <button type="button" class="button-secondary" id="form-mode-btn">📝 Form Mode</button>
                        <button type="button" class="button-secondary" id="json-mode-btn" style="margin-left: var(--spacing-sm);">📄 JSON Mode</button>
                    </div>
                    
                    <!-- JSON Input Mode -->
                    <div id="json-input-container" style="display: none;">
                        <h3>Quick Create from JSON</h3>
                        <p>Paste your poll JSON below or use the example template:</p>
                        
                        <div class="flex-row" style="margin-bottom: var(--spacing-sm);">
                            <button type="button" class="button-secondary" id="load-example-btn">Load Example</button>
                            <button type="button" class="button-secondary" id="clear-json-btn" style="margin-left: var(--spacing-sm);">Clear</button>
                        </div>
                        
                        <textarea id="json-input" class="textarea" rows="20" placeholder="Paste your poll JSON here..."></textarea>
                        
                        <div class="flex-row" style="margin-top: var(--spacing-md);">
                            <button type="button" class="button" id="create-from-json-btn">Create Poll from JSON</button>
                            <button type="button" class="button-secondary" id="validate-json-btn" style="margin-left: var(--spacing-sm);">Validate JSON</button>
                        </div>
                    </div>
                    
                    <!-- Form Input Mode -->
                    <form id="poll-form"
                        <div class="flex-column">
                            <label>${Constants.Strings.POLL_TITLE_LABEL}</label>
                            <input class="input" type="text" id="poll-title" placeholder="Enter poll title" required>
                            
                            <label>${Constants.Strings.POLL_DESCRIPTION_LABEL}</label>
                            <textarea class="textarea" id="poll-description" placeholder="Enter poll description (optional)"></textarea>
                            
                            <h3 class="subtitle">Voting Options</h3>
                            
                            <div id="options-container">
                                <div class="option-card">
                                    <div class="flex-row">
                                        <h4 class="flex-1">Option 1</h4>
                                    </div>
                                    <label>${Constants.Strings.OPTION_TITLE_LABEL}</label>
                                    <input class="input option-title" type="text" placeholder="Option title" required>
                                    <label>${Constants.Strings.OPTION_DESCRIPTION_LABEL}</label>
                                    <textarea class="textarea option-description" placeholder="Option description (optional)"></textarea>
                                    <label>${Constants.Strings.OPTION_URL_LABEL}</label>
                                    <input class="input option-url" type="url" placeholder="https://example.com (optional)">
                                    
                                    <label>Image URL (Optional)</label>
                                    <input class="input option-image-url" type="url" placeholder="https://example.com/image.jpg (optional)">
                                    
                                    <label>Quick Facts (Optional)</label>
                                    <div class="quick-facts-input">
                                        <div class="flex-row">
                                            <input class="input quick-fact-key" type="text" placeholder="Fact name" style="flex: 1; margin-right: 8px;">
                                            <input class="input quick-fact-value" type="text" placeholder="Fact value" style="flex: 1; margin-right: 8px;">
                                            <button type="button" class="button-secondary add-fact-btn">+</button>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="option-card">
                                    <div class="flex-row">
                                        <h4 class="flex-1">Option 2</h4>
                                    </div>
                                    <label>${Constants.Strings.OPTION_TITLE_LABEL}</label>
                                    <input class="input option-title" type="text" placeholder="Option title" required>
                                    <label>${Constants.Strings.OPTION_DESCRIPTION_LABEL}</label>
                                    <textarea class="textarea option-description" placeholder="Option description (optional)"></textarea>
                                    <label>${Constants.Strings.OPTION_URL_LABEL}</label>
                                    <input class="input option-url" type="url" placeholder="https://example.com (optional)">
                                    
                                    <label>Image URL (Optional)</label>
                                    <input class="input option-image-url" type="url" placeholder="https://example.com/image.jpg (optional)">
                                    
                                    <label>Quick Facts (Optional)</label>
                                    <div class="quick-facts-input">
                                        <div class="flex-row">
                                            <input class="input quick-fact-key" type="text" placeholder="Fact name" style="flex: 1; margin-right: 8px;">
                                            <input class="input quick-fact-value" type="text" placeholder="Fact value" style="flex: 1; margin-right: 8px;">
                                            <button type="button" class="button-secondary add-fact-btn">+</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="flex-row">
                                <button type="button" class="button-secondary" id="add-option-btn">Add Option</button>
                                <button type="button" class="button-danger" id="remove-option-btn">Remove Last Option</button>
                            </div>
                            
                            <button type="submit" class="button">${Constants.Strings.CREATE_BUTTON}</button>
                        </div>
                    </form>
                </div>
            </div>
        """.trimIndent()
        
        setupCreatePollForm()
        setupJsonMode()
    }
    
    private fun setupJsonMode() {
        val formModeBtn = document.getElementById("form-mode-btn") as? HTMLButtonElement
        val jsonModeBtn = document.getElementById("json-mode-btn") as? HTMLButtonElement
        val jsonContainer = document.getElementById("json-input-container") as? HTMLElement
        val formContainer = document.getElementById("poll-form") as? HTMLElement
        val jsonInput = document.getElementById("json-input") as? HTMLTextAreaElement
        val loadExampleBtn = document.getElementById("load-example-btn") as? HTMLButtonElement
        val clearJsonBtn = document.getElementById("clear-json-btn") as? HTMLButtonElement
        val createFromJsonBtn = document.getElementById("create-from-json-btn") as? HTMLButtonElement
        val validateJsonBtn = document.getElementById("validate-json-btn") as? HTMLButtonElement
        val messagesDiv = document.getElementById("messages") as HTMLElement
        
        // Mode switching
        formModeBtn?.addEventListener("click", { _ ->
            jsonContainer?.style?.setProperty("display", "none")
            formContainer?.style?.setProperty("display", "block")
            formModeBtn.className = "button"
            jsonModeBtn?.let { it.className = "button-secondary" }
        })
        
        jsonModeBtn?.addEventListener("click", { _ ->
            jsonContainer?.style?.setProperty("display", "block")
            formContainer?.style?.setProperty("display", "none")
            jsonModeBtn.className = "button"
            formModeBtn?.let { it.className = "button-secondary" }
        })
        
        // Load example JSON
        loadExampleBtn?.addEventListener("click", { _ ->
            val exampleJson = """{
  "title": "Best Vacation Rental for Summer 2024",
  "description": "Help us choose the perfect vacation rental for our family trip. Each option has been carefully researched with pricing, amenities, and detailed information.",
  "options": [
    {
      "id": "beachfront-villa",
      "title": "Luxury Beachfront Villa",
      "description": "Stunning oceanfront property with private beach access, perfect for families who want luxury and convenience.",
      "url": "https://example.com/beachfront-villa",
      "imageUrl": "https://picsum.photos/800/600?random=1",
      "quickFacts": {
        "Total Price": "$4,200 (7 nights)",
        "Price Per Night": "$600",
        "Bedrooms": "4 bedrooms, 3 bathrooms",
        "Max Guests": "8 people",
        "Beach Access": "Private beach (50 feet)",
        "Pool": "Infinity pool with ocean view",
        "Hot Tub": "Yes - oceanfront deck",
        "Kitchen": "Gourmet kitchen, fully equipped",
        "Parking": "2-car garage + driveway",
        "WiFi Speed": "500 Mbps fiber",
        "Check-in": "4:00 PM",
        "Check-out": "11:00 AM",
        "Cleaning Fee": "$150",
        "Security Deposit": "$500 (refundable)",
        "Pros": "Private beach, luxury amenities, great views",
        "Cons": "Most expensive, 45min from airport",
        "Host Rating": "4.9/5 (127 reviews)",
        "Cancellation": "Free until 14 days before"
      }
    },
    {
      "id": "mountain-cabin",
      "title": "Cozy Mountain Cabin Retreat",
      "description": "Rustic charm meets modern comfort in this secluded mountain cabin with hiking trails and stunning valley views.",
      "url": "https://example.com/mountain-cabin",
      "imageUrl": "https://picsum.photos/800/600?random=2",
      "quickFacts": {
        "Total Price": "$2,100 (7 nights)",
        "Price Per Night": "$300",
        "Bedrooms": "3 bedrooms, 2 bathrooms",
        "Max Guests": "6 people",
        "Elevation": "3,200 feet",
        "Pool": "No pool",
        "Hot Tub": "Yes - outdoor deck with mountain views",
        "Kitchen": "Full kitchen with wood-burning stove",
        "Parking": "Gravel driveway (2 cars)",
        "WiFi Speed": "100 Mbps",
        "Fireplace": "Stone fireplace with firewood included",
        "Hiking Trails": "3 trails within 0.5 miles",
        "Nearest Town": "15 minutes to village",
        "Grocery Store": "20 minutes drive",
        "Cleaning Fee": "$75",
        "Security Deposit": "$300 (refundable)",
        "Pros": "Great value, peaceful, hiking access",
        "Cons": "Remote location, no pool, gravel road",
        "Host Rating": "4.7/5 (89 reviews)",
        "Cancellation": "Free until 7 days before"
      }
    },
    {
      "id": "city-penthouse",
      "title": "Downtown Penthouse Loft",
      "description": "Modern urban retreat in the heart of the city with rooftop terrace and walking distance to restaurants and attractions.",
      "url": "https://example.com/city-penthouse",
      "imageUrl": "https://picsum.photos/800/600?random=3",
      "quickFacts": {
        "Total Price": "$3,500 (7 nights)",
        "Price Per Night": "$500",
        "Bedrooms": "2 bedrooms, 2 bathrooms",
        "Max Guests": "4 people",
        "Floor": "25th floor penthouse",
        "Pool": "Building rooftop pool (shared)",
        "Hot Tub": "Building rooftop hot tub (shared)",
        "Kitchen": "Modern kitchen with city views",
        "Parking": "Valet parking ($25/night)",
        "WiFi Speed": "1 Gbps fiber",
        "Rooftop Terrace": "Private 400 sq ft terrace",
        "City Views": "360-degree skyline views",
        "Restaurants": "50+ within 3 blocks",
        "Public Transit": "Subway 2 blocks away",
        "Gym Access": "Building gym included",
        "Concierge": "24/7 concierge service",
        "Cleaning Fee": "$100",
        "Security Deposit": "$400 (refundable)",
        "Pros": "City convenience, modern amenities, great views",
        "Cons": "No private pool, expensive parking, urban noise",
        "Host Rating": "4.6/5 (203 reviews)",
        "Cancellation": "Free until 10 days before"
      }
    },
    {
      "id": "lakeside-cottage",
      "title": "Charming Lakeside Cottage",
      "description": "Quaint waterfront cottage with dock access, perfect for fishing, kayaking, and peaceful lake activities.",
      "url": "https://example.com/lakeside-cottage",
      "imageUrl": "https://picsum.photos/800/600?random=4",
      "quickFacts": {
        "Total Price": "$1,750 (7 nights)",
        "Price Per Night": "$250",
        "Bedrooms": "2 bedrooms, 1 bathroom",
        "Max Guests": "4 people",
        "Lake Access": "Private dock and beach area",
        "Pool": "No pool (lake swimming)",
        "Hot Tub": "No",
        "Kitchen": "Compact but fully equipped",
        "Parking": "Dirt driveway (2 cars)",
        "WiFi Speed": "50 Mbps",
        "Dock": "Private 20-foot dock",
        "Kayaks": "2 kayaks included",
        "Fishing": "Great bass and trout fishing",
        "Fire Pit": "Lakeside fire pit with seating",
        "Nearest Store": "10 minutes to general store",
        "Boat Rental": "Marina 5 minutes away",
        "Cleaning Fee": "$50",
        "Security Deposit": "$200 (refundable)",
        "Pros": "Most affordable, peaceful, great fishing",
        "Cons": "Smallest space, no hot tub, basic amenities",
        "Host Rating": "4.8/5 (67 reviews)",
        "Cancellation": "Free until 5 days before"
      }
    }
  ]
}"""
            jsonInput?.let { it.value = exampleJson }
        })
        
        // Clear JSON
        clearJsonBtn?.addEventListener("click", { _ ->
            jsonInput?.let { it.value = "" }
            messagesDiv.innerHTML = ""
        })
        
        // Validate JSON
        validateJsonBtn?.addEventListener("click", { _ ->
            val jsonText = jsonInput?.value ?: ""
            if (jsonText.isBlank()) {
                messagesDiv.innerHTML = """<div class="error-message">Please enter JSON to validate</div>"""
                return@addEventListener
            }
            
            try {
                js("JSON.parse(jsonText)")
                messagesDiv.innerHTML = """<div class="success-message">✅ JSON is valid!</div>"""
            } catch (e: Exception) {
                messagesDiv.innerHTML = """<div class="error-message">❌ Invalid JSON: ${e.message}</div>"""
            }
        })
        
        // Create from JSON
        createFromJsonBtn?.addEventListener("click", { _ ->
            val jsonText = jsonInput?.value ?: ""
            if (jsonText.isBlank()) {
                messagesDiv.innerHTML = """<div class="error-message">Please enter JSON to create poll</div>"""
                return@addEventListener
            }
            
            try {
                val pollData = js("JSON.parse(jsonText)")
                handleCreatePollFromJson(pollData)
            } catch (e: Exception) {
                messagesDiv.innerHTML = """<div class="error-message">❌ Invalid JSON: ${e.message}</div>"""
            }
        })
    }
    
    private fun setupCreatePollForm() {
        val form = document.getElementById("poll-form") as HTMLFormElement
        val addBtn = document.getElementById("add-option-btn") as HTMLButtonElement
        val removeBtn = document.getElementById("remove-option-btn") as HTMLButtonElement
        val container = document.getElementById("options-container") as HTMLElement
        
        var optionCount = 2
        
        addBtn.addEventListener("click", { _ ->
            optionCount++
            val newOption = document.createElement("div")
            newOption.className = "option-card"
            newOption.innerHTML = """
                <div class="flex-row">
                    <h4 class="flex-1">Option $optionCount</h4>
                </div>
                <label>${Constants.Strings.OPTION_TITLE_LABEL}</label>
                <input class="input option-title" type="text" placeholder="Option title" required>
                <label>${Constants.Strings.OPTION_DESCRIPTION_LABEL}</label>
                <textarea class="textarea option-description" placeholder="Option description (optional)"></textarea>
                <label>${Constants.Strings.OPTION_URL_LABEL}</label>
                <input class="input option-url" type="url" placeholder="https://example.com (optional)">
                
                <label>Image URL (Optional)</label>
                <input class="input option-image-url" type="url" placeholder="https://example.com/image.jpg (optional)">
                
                <label>Quick Facts (Optional)</label>
                <div class="quick-facts-input">
                    <div class="flex-row">
                        <input class="input quick-fact-key" type="text" placeholder="Fact name" style="flex: 1; margin-right: 8px;">
                        <input class="input quick-fact-value" type="text" placeholder="Fact value" style="flex: 1; margin-right: 8px;">
                        <button type="button" class="button-secondary add-fact-btn">+</button>
                    </div>
                </div>
            """.trimIndent()
            container.appendChild(newOption)
            
            // Setup quick facts handler for the new option
            val newAddFactBtn = newOption.querySelector(".add-fact-btn") as? HTMLButtonElement
            newAddFactBtn?.addEventListener("click", { _ ->
                val factContainer = newAddFactBtn.parentElement?.parentElement as? HTMLElement
                if (factContainer != null) {
                    val newFactRow = document.createElement("div")
                    newFactRow.className = "flex-row"
                    newFactRow.setAttribute("style", "margin-top: 8px")
                    newFactRow.innerHTML = """
                        <input class="input quick-fact-key" type="text" placeholder="Fact name" style="flex: 1; margin-right: 8px;">
                        <input class="input quick-fact-value" type="text" placeholder="Fact value" style="flex: 1; margin-right: 8px;">
                        <button type="button" class="button-danger" onclick="this.parentElement.remove()">-</button>
                    """
                    factContainer.appendChild(newFactRow)
                }
            })
            
            removeBtn.style.display = if (optionCount > 2) "inline-block" else "none"
        })
        
        removeBtn.addEventListener("click", { _ ->
            if (optionCount > 2) {
                container.removeChild(container.lastElementChild!!)
                optionCount--
                removeBtn.style.display = if (optionCount > 2) "inline-block" else "none"
            }
        })
        
        // Initially hide remove button
        removeBtn.style.display = "none"
        
        // Setup quick facts add buttons
        setupQuickFactsButtons()
        
        form.addEventListener("submit", { event ->
            event.preventDefault()
            handleCreatePoll()
        })
    }
    
    private fun setupQuickFactsButtons() {
        val addFactBtns = document.querySelectorAll(".add-fact-btn")
        for (i in 0 until addFactBtns.length) {
            val btn = addFactBtns[i] as HTMLButtonElement
            btn.addEventListener("click", { _ ->
                val container = btn.parentElement?.parentElement as? HTMLElement
                if (container != null) {
                    val newFactRow = document.createElement("div")
                    newFactRow.className = "flex-row"
                    newFactRow.setAttribute("style", "margin-top: 8px")
                    newFactRow.innerHTML = """
                        <input class="input quick-fact-key" type="text" placeholder="Fact name" style="flex: 1; margin-right: 8px;">
                        <input class="input quick-fact-value" type="text" placeholder="Fact value" style="flex: 1; margin-right: 8px;">
                        <button type="button" class="button-danger" onclick="this.parentElement.remove()">-</button>
                    """
                    container.appendChild(newFactRow)
                }
            })
        }
    }
    
    private fun handleCreatePollFromJson(pollData: dynamic) {
        val messagesDiv = document.getElementById("messages") as HTMLElement
        
        GlobalScope.launch {
            try {
                messagesDiv.innerHTML = """<div class="info-message">Creating poll from JSON...</div>"""
                
                // Convert dynamic JSON to CreatePollRequest
                val title = pollData.title as? String ?: throw Exception("Title is required")
                val description = pollData.description as? String
                val optionsArray = pollData.options
                
                if (optionsArray == null) {
                    throw Exception("Options array is required")
                }
                
                val arrayLength = js("optionsArray.length") as Int
                val options = mutableListOf<VotingOption>()
                
                for (i in 0 until arrayLength) {
                    val optionData = optionsArray[i]
                    val quickFactsObj = optionData.quickFacts
                    val quickFacts = mutableMapOf<String, String>()
                    
                    if (quickFactsObj != null) {
                        val keys = js("Object.keys(quickFactsObj)") as Array<String>
                        for (key in keys) {
                            val value = quickFactsObj[key] as? String
                            if (value != null) {
                                quickFacts[key] = value
                            }
                        }
                    }
                    
                    // Debug logging
                    console.log("Processing option: ${optionData.title}")
                    console.log("Has pros array: ${optionData.pros != null}")
                    console.log("Has cons array: ${optionData.cons != null}")
                    
                    // Process pros array
                    val prosArray = optionData.pros
                    if (prosArray != null && js("Array.isArray(prosArray)") as Boolean) {
                        val prosLength = prosArray.length as Int
                        val prosList = mutableListOf<String>()
                        for (j in 0 until prosLength) {
                            prosList.add(prosArray[j] as String)
                        }
                        quickFacts["Pros"] = prosList.joinToString(", ")
                        console.log("Added pros: ${quickFacts["Pros"]}")
                    }
                    
                    // Process cons array  
                    val consArray = optionData.cons
                    if (consArray != null && js("Array.isArray(consArray)") as Boolean) {
                        val consLength = consArray.length as Int
                        val consList = mutableListOf<String>()
                        for (j in 0 until consLength) {
                            consList.add(consArray[j] as String)
                        }
                        quickFacts["Cons"] = consList.joinToString(", ")
                        console.log("Added cons: ${quickFacts["Cons"]}")
                    }
                    
                    options.add(VotingOption(
                        id = optionData.id as? String ?: generateId(),
                        title = optionData.title as? String ?: throw Exception("Option title is required"),
                        description = optionData.description as? String,
                        url = optionData.url as? String,
                        imageUrl = optionData.imageUrl as? String,
                        processedImagePath = null,
                        quickFacts = quickFacts
                    ))
                }
                
                if (options.size < 2) {
                    messagesDiv.innerHTML = """<div class="error-message">At least 2 options are required</div>"""
                    return@launch
                }
                
                val request = CreatePollRequest(
                    title = title,
                    description = description,
                    options = options
                )
                
                val poll = ApiClient.createPoll(request)
                
                messagesDiv.innerHTML = """
                    <div class="success-message">
                        <h3>✅ Poll Created Successfully from JSON!</h3>
                        <p><strong>Poll ID:</strong> ${poll.id}</p>
                        <p>Created ${options.size} options with images and quick facts.</p>
                        <div class="flex-row" style="margin-top: var(--spacing-sm);">
                            <button class="button-secondary" onclick="window.history.pushState(null, '', '/vote?id=${poll.id}'); window.location.reload();">Go to Poll</button>
                            <button class="button-secondary" onclick="window.history.pushState(null, '', '/admin?id=${poll.id}'); window.location.reload();" style="margin-left: var(--spacing-sm);">Admin Panel</button>
                        </div>
                    </div>
                """
                
            } catch (e: Exception) {
                messagesDiv.innerHTML = """<div class="error-message">Failed to create poll: ${e.message}</div>"""
            }
        }
    }
    
    private fun handleCreatePoll() {
        val messagesDiv = document.getElementById("messages") as HTMLElement
        messagesDiv.innerHTML = """<div class="loading-container"><div class="loading"></div><span style="margin-left: 10px;">Creating poll...</span></div>"""
        
        GlobalScope.launch {
            try {
                val title = (document.getElementById("poll-title") as HTMLInputElement).value
                val description = (document.getElementById("poll-description") as HTMLTextAreaElement).value.takeIf { it.isNotBlank() }
                
                val options = mutableListOf<VotingOption>()
                val optionCards = document.querySelectorAll(".option-card")
                
                for (i in 0 until optionCards.length) {
                    val card = optionCards[i] as Element
                    val titleInputs = card.querySelectorAll(".option-title")
                    val descInputs = card.querySelectorAll(".option-description")
                    val urlInputs = card.querySelectorAll(".option-url")
                    val imageUrlInputs = card.querySelectorAll(".option-image-url")
                    
                    if (titleInputs.length > 0) {
                        val titleInput = titleInputs[0] as HTMLInputElement
                        val descInput = descInputs[0] as HTMLTextAreaElement
                        val urlInput = urlInputs[0] as HTMLInputElement
                        val imageUrlInput = if (imageUrlInputs.length > 0) imageUrlInputs[0] as HTMLInputElement else null
                        
                        // Collect quick facts
                        val quickFacts = mutableMapOf<String, String>()
                        val factKeyInputs = card.querySelectorAll(".quick-fact-key")
                        val factValueInputs = card.querySelectorAll(".quick-fact-value")
                        
                        for (j in 0 until factKeyInputs.length) {
                            val keyInput = factKeyInputs[j] as HTMLInputElement
                            val valueInput = if (j < factValueInputs.length) factValueInputs[j] as HTMLInputElement else null
                            
                            if (keyInput.value.isNotBlank() && valueInput != null && valueInput.value.isNotBlank()) {
                                quickFacts[keyInput.value] = valueInput.value
                            }
                        }
                        
                        if (titleInput.value.isNotBlank()) {
                            options.add(VotingOption(
                                id = generateId(),
                                title = titleInput.value,
                                description = descInput.value.takeIf { it.isNotBlank() },
                                url = urlInput.value.takeIf { it.isNotBlank() },
                                imageUrl = imageUrlInput?.value?.takeIf { it.isNotBlank() },
                                processedImagePath = null,
                                quickFacts = quickFacts
                            ))
                        }
                    }
                }
                
                if (options.size < 2) {
                    messagesDiv.innerHTML = """<div class="error-message">Please provide at least 2 options with titles</div>"""
                    return@launch
                }
                
                val request = CreatePollRequest(title, description, options)
                val poll = ApiClient.createPoll(request)
                
                messagesDiv.innerHTML = """<div class="success-message">Poll created successfully! Poll ID: ${poll.id}<br>Created ${options.size} options. Redirecting to voting page...</div>"""
                
                window.setTimeout({
                    window.history.pushState(null, "", "/vote?id=${poll.id}")
                    render()
                }, 2000)
                
            } catch (e: Exception) {
                messagesDiv.innerHTML = """<div class="error-message">Failed to create poll: ${e.message}</div>"""
            }
        }
    }
    
    private fun renderVotingPage(pollId: String) {
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <div class="loading-container"><div class="loading"></div><span style="margin-left: 10px;">Loading poll...</span></div>
            </div>
        """.trimIndent()
        
        GlobalScope.launch {
            try {
                val poll = ApiClient.getPoll(pollId)
                renderVotingPageContent(poll, pollId)
            } catch (e: Exception) {
                root.innerHTML = """
                    <div class="container">
                        <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                        <div class="error-message">Failed to load poll: ${e.message}</div>
                        <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Go Home</button>
                    </div>
                """.trimIndent()
            }
        }
    }
    
    private fun renderVotingPageContent(poll: Poll, pollId: String) {
        val pollDesc = poll.description?.let { "<p>$it</p>" } ?: ""
        val statusClass = getStatusClass(poll.status)
        val statusText = getStatusText(poll.status)
        
        val votingContent = if (poll.status == PollStatus.IN_PROGRESS) {
            """
            <div id="messages"></div>
            <form id="vote-form">
                <div class="flex-column">
                    <label>${Constants.Strings.VOTER_NAME_LABEL}</label>
                    <input class="input" type="text" id="voter-name" placeholder="Enter your name" required>
                    
                    <h3 class="subtitle">Rate each option (0-5 stars)</h3>
                    <p><strong>Total Options:</strong> ${poll.options.size}</p>
                    
                    ${poll.options.mapIndexed { index, option -> renderVotingOption(option, index) }.joinToString("")}
                    
                    <button type="submit" class="button">${Constants.Strings.SUBMIT_VOTE_BUTTON}</button>
                </div>
            </form>
            """
        } else {
            when (poll.status) {
                PollStatus.NOT_STARTED -> "<p>This poll has not started yet. Please wait for the administrator to start it.</p>"
                PollStatus.ENDED -> """
                    <p>This poll has ended.</p>
                    <p><strong>Total Options:</strong> ${poll.options.size}</p>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/status?id=$pollId'); window.location.reload();">View Results</button>
                """
                else -> ""
            }
        }
        
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <nav class="navigation">
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Create Poll</button>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/admin?id=$pollId'); window.location.reload();">Admin Panel</button>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/status?id=$pollId'); window.location.reload();">View Status</button>
                </nav>
                <div class="card">
                    <h2 class="subtitle">${poll.title}</h2>
                    $pollDesc
                    
                    <div class="flex-row">
                        <span>Status: </span>
                        <span class="status-badge $statusClass">$statusText</span>
                    </div>
                    
                    $votingContent
                </div>
            </div>
        """.trimIndent()
        
        if (poll.status == PollStatus.IN_PROGRESS) {
            setupVotingForm(pollId)
        }
    }
    
    private fun renderVotingOption(option: VotingOption, index: Int): String {
        val optionDesc = option.description?.let { "<p>$it</p>" } ?: ""
        val optionUrl = option.url?.let { """<p><a class="link" href="$it" target="_blank" rel="noopener noreferrer">🔗 Visit ${option.title}</a></p>""" } ?: ""
        val optionImage = option.processedImagePath?.let { 
            """<div class="option-image-container"><img class="option-image" src="$it" alt="${option.title}" /></div>""" 
        } ?: ""
        
        val quickFactsHtml = if (option.quickFacts.isNotEmpty()) {
            val factsItems = option.quickFacts.entries.joinToString("") { (key, value) ->
                """
                <div class="quick-fact-item">
                    <span class="quick-fact-key">$key:</span>
                    <span class="quick-fact-value">$value</span>
                </div>
                """
            }
            
            """
            <button type="button" class="quick-facts-toggle" onclick="document.getElementById('quick-facts-$index').classList.toggle('quick-facts-hidden')" title="Show/Hide Quick Facts">?</button>
            <div class="quick-facts quick-facts-hidden" id="quick-facts-$index">
                <h4 style="margin-top: 0; color: var(--primary);">Quick Facts</h4>
                $factsItems
            </div>
            """
        } else ""
        
        return """
            <div class="option-card">
                $quickFactsHtml
                <h3>${option.title}</h3>
                $optionDesc
                $optionUrl
                $optionImage
                
                <div class="slider-container">
                    <label id="rating-label-$index">${Constants.Strings.RATING_LABEL}: 0 stars</label>
                    <input class="slider" type="range" min="0" max="5" step="1" value="0" 
                           id="rating-$index" data-option-id="${option.id}">
                    <div class="star-display" id="stars-$index">
                        <span class="star empty">★</span>
                        <span class="star empty">★</span>
                        <span class="star empty">★</span>
                        <span class="star empty">★</span>
                        <span class="star empty">★</span>
                    </div>
                </div>
            </div>
        """.trimIndent()
    }
    
    private fun setupVotingForm(pollId: String) {
        val form = document.getElementById("vote-form") as HTMLFormElement
        
        // Setup rating sliders
        val sliders = document.querySelectorAll(".slider")
        for (i in 0 until sliders.length) {
            val slider = sliders[i] as HTMLInputElement
            slider.addEventListener("input", { _ ->
                val rating = slider.value.toInt()
                val index = slider.id.substringAfter("rating-")
                updateStarDisplay(index.toInt(), rating)
            })
        }
        
        form.addEventListener("submit", { event ->
            event.preventDefault()
            handleSubmitVote(pollId)
        })
    }
    
    private fun updateStarDisplay(index: Int, rating: Int) {
        val label = document.getElementById("rating-label-$index") as HTMLElement
        label.textContent = "${Constants.Strings.RATING_LABEL}: $rating stars"
        
        val starsContainer = document.getElementById("stars-$index") as HTMLElement
        val stars = starsContainer.children
        for (i in 0 until stars.length) {
            val star = stars[i] as HTMLElement
            star.className = if (i < rating) "star filled" else "star empty"
        }
    }
    
    private fun handleSubmitVote(pollId: String) {
        val messagesDiv = document.getElementById("messages") as HTMLElement
        messagesDiv.innerHTML = """<div class="loading-container"><div class="loading"></div><span style="margin-left: 10px;">Submitting vote...</span></div>"""
        
        GlobalScope.launch {
            try {
                val voterName = (document.getElementById("voter-name") as HTMLInputElement).value
                
                val ratings = mutableMapOf<String, Int>()
                val sliders = document.querySelectorAll(".slider")
                for (i in 0 until sliders.length) {
                    val slider = sliders[i] as HTMLInputElement
                    val optionId = slider.getAttribute("data-option-id") ?: ""
                    ratings[optionId] = slider.value.toInt()
                }
                
                val voteRequest = VoteRequest(voterName, ratings)
                ApiClient.submitVote(pollId, voteRequest)
                
                messagesDiv.innerHTML = """<div class="success-message">Vote submitted successfully!<br>Redirecting to results...</div>"""
                
                window.setTimeout({
                    window.history.pushState(null, "", "/status?id=$pollId")
                    render()
                }, 2000)
                
            } catch (e: Exception) {
                messagesDiv.innerHTML = """<div class="error-message">Failed to submit vote: ${e.message}</div>"""
            }
        }
    }
    
    private fun renderStatusPage(pollId: String) {
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <div class="loading-container"><div class="loading"></div><span style="margin-left: 10px;">Loading poll status...</span></div>
            </div>
        """.trimIndent()
        
        GlobalScope.launch {
            try {
                val statusResponse = ApiClient.getPollStatus(pollId)
                renderStatusPageContent(statusResponse, pollId)
            } catch (e: Exception) {
                root.innerHTML = """
                    <div class="container">
                        <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                        <div class="error-message">Failed to load poll status: ${e.message}</div>
                        <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Go Home</button>
                    </div>
                """.trimIndent()
            }
        }
    }
    
    private fun renderStatusPageContent(response: PollStatusResponse, pollId: String) {
        val poll = response.poll
        val pollDesc = poll.description?.let { "<p>$it</p>" } ?: ""
        val statusClass = getStatusClass(poll.status)
        val statusText = getStatusText(poll.status)
        
        val statusContent = when (poll.status) {
            PollStatus.NOT_STARTED -> "<p>Poll has not started yet. Check back later.</p>"
            PollStatus.IN_PROGRESS -> {
                val voters = response.voterNames ?: emptyList()
                val votersHtml = if (voters.isNotEmpty()) {
                    "<ul>" + voters.joinToString("") { "<li>$it</li>" } + "</ul>"
                } else {
                    "<p>No votes yet.</p>"
                }
                """
                <div class="card">
                    <h3>${Constants.Strings.VOTERS_COUNT_LABEL} (${voters.size})</h3>
                    $votersHtml
                </div>
                """
            }
            PollStatus.ENDED -> {
                val voters = response.voterNames ?: emptyList()
                val votersHtml = if (voters.isNotEmpty()) {
                    "<ul>" + voters.joinToString("") { "<li>$it</li>" } + "</ul>"
                } else {
                    "<p>No votes cast.</p>"
                }
                
                val resultsHtml = response.results?.let { results ->
                    val winnerDesc = results.winner.description?.let { "<p>$it</p>" } ?: ""
                    val winnerUrl = results.winner.url?.let { """<p><a class="link" href="$it" target="_blank">$it</a></p>""" } ?: ""
                    
                    val ratingsHtml = poll.options.sortedByDescending { results.averageRatings[it.id] ?: 0.0 }.joinToString("") { option ->
                        val rating = results.averageRatings[option.id] ?: 0.0
                        val optionDesc = option.description?.let { "<p>$it</p>" } ?: ""
                        val ratingText = "${(rating * 10).toInt() / 10.0}"
                        
                        val starsHtml = (0..4).joinToString("") { index ->
                            val starClass = if (index < rating.toInt()) "star filled" else "star empty"
                            """<span class="$starClass">★</span>"""
                        }
                        
                        """
                        <div class="option-card rating-card">
                            <div class="flex-row">
                                <div class="flex-1">
                                    <h3>${option.title}</h3>
                                    $optionDesc
                                </div>
                                <div class="rating-display">
                                    <div class="rating-number">$ratingText</div>
                                    <div class="star-display">$starsHtml</div>
                                    <span class="rating-text"> / 5 stars</span>
                                </div>
                            </div>
                        </div>
                        """
                    }
                    
                    """
                    <div class="card winner-section">
                        <h3 class="subtitle">${Constants.Strings.WINNER_LABEL}</h3>
                        <div class="option-card winner-card">
                            <h3>${results.winner.title}</h3>
                            $winnerDesc
                            $winnerUrl
                            <p class="preference-count">${results.winnerPreferenceCount} ${Constants.Strings.PREFERENCE_COUNT_LABEL}</p>
                        </div>
                        
                        <h3 class="subtitle">${Constants.Strings.AVERAGE_RATINGS_LABEL}</h3>
                        $ratingsHtml
                    </div>
                    """
                } ?: ""
                
                """
                <div class="card">
                    <h3>${Constants.Strings.VOTERS_COUNT_LABEL} (${voters.size})</h3>
                    $votersHtml
                </div>
                $resultsHtml
                """
            }
        }
        
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <nav class="navigation">
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Create Poll</button>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/admin?id=$pollId'); window.location.reload();">Admin Panel</button>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/vote?id=$pollId'); window.location.reload();">Back to Poll</button>
                </nav>
                <div class="card">
                    <h2 class="subtitle">${Constants.Strings.POLL_STATUS_TITLE}</h2>
                    <h3 class="subtitle">${poll.title}</h3>
                    $pollDesc
                    
                    <div class="flex-row">
                        <span>Status: </span>
                        <span class="status-badge $statusClass">$statusText</span>
                    </div>
                    
                    <p><strong>Total Options:</strong> ${poll.options.size}</p>
                    
                    $statusContent
                    
                    <div class="flex-row">
                        <button class="button-secondary" onclick="window.location.reload()">Refresh</button>
                    </div>
                </div>
            </div>
        """.trimIndent()
    }
    
    private fun generateId(): String {
        val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
        return (1..8).map { chars[Random.nextInt(chars.length)] }.joinToString("")
    }
    
    private fun getStatusClass(status: PollStatus): String = when (status) {
        PollStatus.NOT_STARTED -> "status-not-started"
        PollStatus.IN_PROGRESS -> "status-in-progress"
        PollStatus.ENDED -> "status-ended"
    }
    
    private fun getStatusText(status: PollStatus): String = when (status) {
        PollStatus.NOT_STARTED -> Constants.Strings.POLL_NOT_STARTED
        PollStatus.IN_PROGRESS -> Constants.Strings.POLL_IN_PROGRESS
        PollStatus.ENDED -> Constants.Strings.POLL_ENDED
    }
    
    private fun renderAdminPage(pollId: String) {
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <div class="loading-container"><div class="loading"></div><span style="margin-left: 10px;">Loading admin panel...</span></div>
            </div>
        """.trimIndent()
        
        GlobalScope.launch {
            try {
                val poll = ApiClient.getPoll(pollId)
                renderAdminPageContent(poll, pollId)
            } catch (e: Exception) {
                root.innerHTML = """
                    <div class="container">
                        <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                        <div class="error-message">Failed to load admin panel: ${e.message}</div>
                        <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Go Home</button>
                    </div>
                """.trimIndent()
            }
        }
    }
    
    private fun renderAdminPageContent(poll: Poll, pollId: String) {
        val pollDesc = poll.description?.let { "<p>$it</p>" } ?: ""
        val statusClass = getStatusClass(poll.status)
        val statusText = getStatusText(poll.status)
        
        val adminControls = when (poll.status) {
            PollStatus.NOT_STARTED -> """
                <div class="card">
                    <h3 class="subtitle">Poll Controls</h3>
                    <p>Poll is ready to start. Click the button below to allow voting.</p>
                    <button class="button" id="start-poll-btn">Start Poll</button>
                    <div id="admin-messages"></div>
                </div>
            """
            PollStatus.IN_PROGRESS -> """
                <div class="card">
                    <h3 class="subtitle">Poll Controls</h3>
                    <p>Poll is currently accepting votes. You can end it at any time to see results.</p>
                    <button class="button-danger" id="end-poll-btn">End Poll</button>
                    <div id="admin-messages"></div>
                </div>
            """
            PollStatus.ENDED -> """
                <div class="card">
                    <h3 class="subtitle">Poll Controls</h3>
                    <p>Poll has ended. Results are available on the status page.</p>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/status?id=$pollId'); window.location.reload();">View Results</button>
                </div>
            """
        }
        
        val optionsHtml = poll.options.mapIndexed { index, option ->
            val optionDesc = option.description?.let { "<p><strong>Description:</strong> $it</p>" } ?: ""
            val optionUrl = option.url?.let { """<p><strong>URL:</strong> <a class="link" href="$it" target="_blank">$it</a></p>""" } ?: ""
            
            """
            <div class="option-card">
                <h4>Option ${index + 1}: ${option.title}</h4>
                $optionDesc
                $optionUrl
                <p><strong>ID:</strong> <code>${option.id}</code></p>
            </div>
            """
        }.joinToString("")
        
        root.innerHTML = """
            <div class="container">
                <h1 class="title">${Constants.Strings.APP_TITLE}</h1>
                <nav class="navigation">
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/'); window.location.reload();">Create Poll</button>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/vote?id=$pollId'); window.location.reload();">Vote Page</button>
                    <button class="button-secondary" onclick="window.history.pushState(null, '', '/status?id=$pollId'); window.location.reload();">Status Page</button>
                </nav>
                
                <div class="card">
                    <h2 class="subtitle">Admin Panel</h2>
                    <h3 class="subtitle">${poll.title}</h3>
                    $pollDesc
                    
                    <div class="flex-row">
                        <span>Status: </span>
                        <span class="status-badge $statusClass">$statusText</span>
                    </div>
                    
                    <p><strong>Poll ID:</strong> <code>$pollId</code></p>
                    <p><strong>Total Options:</strong> ${poll.options.size}</p>
                    <p><strong>Total Votes:</strong> ${poll.votes.size}</p>
                    <p><strong>Created:</strong> ${poll.createdAt}</p>
                </div>
                
                $adminControls
                
                <div class="card">
                    <h3 class="subtitle">Poll Options</h3>
                    <p>These are the voting options for this poll:</p>
                    $optionsHtml
                </div>
                
                <div class="card">
                    <h3 class="subtitle">Direct Links</h3>
                    <p>Share these links with voters:</p>
                    <div class="flex-column">
                        <label>Voting Link:</label>
                        <input class="input" type="text" value="${window.location.origin}/vote?id=$pollId" readonly onclick="this.select()">
                        
                        <label>Status Link:</label>
                        <input class="input" type="text" value="${window.location.origin}/status?id=$pollId" readonly onclick="this.select()">
                        
                        <label>Admin Link:</label>
                        <input class="input" type="text" value="${window.location.origin}/admin?id=$pollId" readonly onclick="this.select()">
                    </div>
                    <p><small>Click on any link to select and copy it.</small></p>
                </div>
                
                <!-- Vote results hidden in admin panel for security -->
            </div>
        """.trimIndent()
        
        setupAdminControls(pollId, poll.status)
    }
    
    private fun renderVotesSummary(poll: Poll): String {
        val votesHtml = poll.votes.mapIndexed { index, vote ->
            val ratingsHtml = vote.ratings.entries.joinToString(", ") { (optionId, rating) ->
                val optionTitle = poll.options.find { it.id == optionId }?.title ?: optionId
                "$optionTitle: $rating★"
            }
            
            """
            <div class="option-card">
                <h4>Vote ${index + 1}: ${vote.voterName}</h4>
                <p><strong>Submitted:</strong> ${vote.timestamp}</p>
                <p><strong>Ratings:</strong> $ratingsHtml</p>
                <p><small><strong>IP:</strong> ${vote.voterIp} | <strong>User Agent:</strong> ${vote.userAgent.take(50)}...</small></p>
            </div>
            """
        }.joinToString("")
        
        return """
            <div class="card">
                <h3 class="subtitle">Submitted Votes (${poll.votes.size})</h3>
                <p>All votes submitted for this poll:</p>
                $votesHtml
            </div>
        """
    }
    
    private fun setupAdminControls(pollId: String, currentStatus: PollStatus) {
        when (currentStatus) {
            PollStatus.NOT_STARTED -> {
                val startBtn = document.getElementById("start-poll-btn") as? HTMLButtonElement
                startBtn?.addEventListener("click", { _ ->
                    updatePollStatusAdmin(pollId, PollStatus.IN_PROGRESS)
                })
            }
            PollStatus.IN_PROGRESS -> {
                val endBtn = document.getElementById("end-poll-btn") as? HTMLButtonElement
                endBtn?.addEventListener("click", { _ ->
                    updatePollStatusAdmin(pollId, PollStatus.ENDED)
                })
            }
            else -> { /* No controls needed for ended polls */ }
        }
    }
    
    private fun updatePollStatusAdmin(pollId: String, newStatus: PollStatus) {
        val messagesDiv = document.getElementById("admin-messages") as HTMLElement
        messagesDiv.innerHTML = """<div class="loading-container"><div class="loading"></div><span style="margin-left: 10px;">Updating poll status...</span></div>"""
        
        GlobalScope.launch {
            try {
                ApiClient.updatePollStatus(pollId, newStatus)
                messagesDiv.innerHTML = """<div class="success-message">Poll status updated successfully!</div>"""
                
                window.setTimeout({
                    window.location.reload()
                }, 1500)
                
            } catch (e: Exception) {
                messagesDiv.innerHTML = """<div class="error-message">Failed to update poll status: ${e.message}</div>"""
            }
        }
    }
}
